import { get } from 'lodash-es';

import { BUILTIN_TOOLS } from '@/Pages/Playground/Playground.constants';
import { EToolChoice } from '@/Pages/Playground/components/manifest/serialized/common/constants';

export interface AnthropicTool {
  name: string;
  description?: string | null;
  input_schema: unknown;
}

export interface OpenAIFunction {
  name?: string | null;
  description?: string | null;
  parameters: unknown;
  strict?: boolean;
}

export interface OpenAITool {
  type: 'function';
  function: OpenAIFunction;
}

export interface OpenAIBuiltinTool {
  type: string;
  [key: string]: unknown;
}

export interface AnthropicBuiltinTool {
  type: string;
  name: string;
  [key: string]: unknown;
}

export type BuiltinTool = OpenAIBuiltinTool | AnthropicBuiltinTool;

export interface OpenAIResponseTool {
  type: string;
  name?: string | null;
  description?: string | null;
  parameters?: unknown;
  strict?: boolean;
}

export interface JsonSchema {
  type: string;
  properties: unknown;
  title?: string;
  description?: string;
  required?: string[];
  additionalProperties?: boolean;
}

export interface VertexAITool {
  functionDeclarations: OpenAIFunction[];
}

export type ToolChoiceParam =
  | EToolChoice
  | { type: EToolChoice }
  | { type: 'tool'; name: string }
  | { type: 'function'; function: { name: string } }
  | string;

export type PromptTool = OpenAITool | BuiltinTool;

export function isAnthropicTool(x: unknown): x is AnthropicTool {
  if (typeof x !== 'object' || x === null) return false;
  if (!('name' in x) || typeof x.name !== 'string') return false;
  if (!('input_schema' in x)) return false;
  return true;
}

export function isAnthropicBuiltinTool(x: unknown): x is AnthropicBuiltinTool {
  if (typeof x !== 'object' || x === null) return false;
  if (!('type' in x) || typeof x.type !== 'string' || x.type === 'function')
    return false;
  if (!('name' in x) || typeof x.name !== 'string') return false;
  return true;
}

export function isOpenAIFunction(x: unknown): x is OpenAIFunction {
  if (typeof x !== 'object' || x === null) return false;
  if (!('name' in x) || typeof x.name !== 'string') return false;
  if (!('parameters' in x)) return false;
  return true;
}

export function isOpenAIBuiltinTool(x: unknown): x is OpenAIBuiltinTool {
  if (typeof x !== 'object' || x === null) return false;
  if (!('type' in x) || typeof x.type !== 'string' || x.type === 'function')
    return false;
  return true;
}

export function isOpenAITool(x: unknown): x is OpenAITool {
  if (typeof x !== 'object' || x === null) return false;
  if (!('type' in x) || x.type !== 'function') return false;
  if (!('function' in x)) return false;
  return isOpenAIFunction(x.function);
}

export function isOpenAIResponseTool(x: unknown): x is OpenAIResponseTool {
  if (typeof x !== 'object' || x === null) return false;
  if (!('type' in x) || typeof x.type !== 'string') return false;
  return (
    [
      'function',
      'computer_preview',
      'web_search_preview',
      'web_search',
      'file_search',
      'mcp',
      'image_generation',
    ].includes(x.type) || x.type.startsWith('web_search_preview_')
  );
}

export function isOpenAIFunctions(x: unknown): x is Array<OpenAIFunction> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every(isOpenAIFunction);
}

export function emptyAnthropicTool(): AnthropicTool {
  return {
    name: '',
    description: '',
    input_schema: {
      type: 'object',
      properties: {},
      required: [],
    },
  };
}

export function emptyOpenAIFunction(): OpenAIFunction {
  return {
    name: '',
    description: '',
    parameters: {
      type: 'object',
      properties: {},
      required: [],
    },
    strict: false,
  };
}

export function emptyOpenAITool(): OpenAITool {
  return {
    type: 'function' as const,
    function: emptyOpenAIFunction(),
  };
}

export function isOpenAIResponseTools(
  x: unknown
): x is Array<OpenAIResponseTool> {
  if (!Array.isArray(x)) return false;
  return x.every(isOpenAIResponseTool);
}

export function isOpenAIResponsesToolsOrBuiltinTools(
  x: unknown
): x is (OpenAIResponseTool | OpenAIBuiltinTool)[] {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every(
    (tool) => isOpenAIResponseTool(tool) || isOpenAIBuiltinTool(tool)
  );
}

export function isOpenAITools(x: unknown): x is Array<OpenAITool> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every(isOpenAITool);
}

export function isAnthropicTools(x: unknown): x is Array<AnthropicTool> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every(isAnthropicTool);
}

export function isAnthropicToolsAndBuiltins(
  x: unknown
): x is Array<AnthropicTool | AnthropicBuiltinTool> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every((tool) => isAnthropicTool(tool) || isBuiltinTool(tool));
}

export function isVertexAITools(x: unknown): x is Array<VertexAITool> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  if (x.length === 0) return false;
  if (typeof x[0] !== 'object' || x[0] === null) return false;
  if (
    !('functionDeclarations' in x[0]) ||
    !Array.isArray(x[0].functionDeclarations)
  )
    return false;
  return x[0].functionDeclarations.every(isOpenAIFunction);
}

export function isBuiltinTool(x: unknown): x is BuiltinTool {
  return isOpenAIBuiltinTool(x) || isAnthropicBuiltinTool(x);
}

export function isPlaygroundTools(x: unknown): x is Array<PromptTool> {
  if (typeof x !== 'object' || x === null) return false;
  if (!Array.isArray(x)) return false;
  return x.every((tool) => isOpenAITool(tool) || isBuiltinTool(tool));
}

export function isOpenAIFunctionCall(
  value: unknown
): value is { name: string } {
  return (
    typeof value === 'object' &&
    value != null &&
    'name' in value &&
    typeof value.name === 'string'
  );
}

export function isOpenAIToolChoice(
  value: unknown
): value is { type: 'function'; function: { name: string } } {
  return (
    get(value, 'type') === 'function' &&
    isOpenAIFunctionCall(get(value, 'function'))
  );
}

export function isAnthropicToolChoice(
  value: unknown
): value is { type: 'tool'; name: string } | { type: EToolChoice } {
  return (
    (get(value, 'type') === 'tool' && typeof get(value, 'name') === 'string') ||
    get(value, 'type') === EToolChoice.AUTO ||
    get(value, 'type') === EToolChoice.ANY
  );
}

export function isGenericToolChoice(
  value: unknown
): value is { type: 'func'; name: string } {
  return (
    get(value, 'type') === 'func' && typeof get(value, 'name') === 'string'
  );
}

export const simplifyToolChoice = (value: ToolChoiceParam | undefined) => {
  if (!value) return undefined;
  if (isOpenAIToolChoice(value)) {
    return value.function.name;
  }
  if (isAnthropicToolChoice(value)) {
    return 'name' in value ? value.name : value.type;
  }
  if (isGenericToolChoice(value)) {
    return value.name;
  }
  return value;
};

export const hasBuiltinTools = (modelId: string) => {
  return Object.keys(BUILTIN_TOOLS).includes(modelId);
};

export const builtinToolsForModelId = (modelId: string) => {
  return BUILTIN_TOOLS[modelId] ?? [];
};
